// 导入必要的React钩子和组件
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import equal from 'fast-deep-equal'; // 用于深度比较对象是否相等
import CardGroup from '../CardGroup/CardGroup';
import ChangeLog from '../ChangeLog/ChangeLog';
import Loading from '../Loading/Loading';
import { fetchFilesList } from '../../api/services';
import styles from './index.module.less';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';

// 定义手册下载链接的配置对象
// 根据不同的区域（中国/全球）提供不同的下载链接
const HANDBOOK_DOWNLOAD_LINKS = {
  // 工业数字化手册
  IndustrialDigitalization_handbook: {
    china: [
      {
        name: '工业数字化产品合集',
        url: 'https://www.inhand.com.cn/wp-content/uploads/Industrial-digitalization.pdf',
      },
      {
        name: '边缘智能解决方案',
        url: 'https://www.inhand.com.cn/wp-content/uploads/Edge-Intelligence-Solution.pdf',
      },
    ],
    global: [
      {
        name: 'Product Catalog - Industrial IoT',
        url: 'https://www.inhand.com/wp-content/uploads/product-catalog_industrial-iot.pdf',
      },
      {
        name: 'Brochure - InHand Edge Intelligence Solution',
        url: 'https://www.inhand.com/wp-content/uploads/inhand-edge-intelligence-solution.pdf',
      },
    ],
  },
  // 企业网络手册
  EnterpriseNetwork_handbook: {
    china: [
      {
        name: '企业网络解决方案',
        url: 'https://www.inhand.com.cn/wp-content/uploads/enterprise-network.pdf',
      },
    ],
    global: [
      {
        name: 'Brochure - Enterprise Network',
        url: 'https://www.inhand.com/wp-content/uploads/inhand-enterprise-network.pdf',
      },
    ],
  },
  // 智慧商业手册
  SmartCommerce_handbook: {
    china: [
      {
        name: '智慧新零售产品合集',
        url: 'https://www.inhand.com.cn/wp-content/uploads/smart-commerce.pdf',
      },
    ],
    global: [
      {
        name: 'InHandGo Grab & Go AI Vending Cooler',
        url: 'https://www.inhand.com/wp-content/uploads/inhandgo-grab-n-go-ai-vending-cooler.pdf',
      },
      {
        name: 'Smart Vending System',
        url: 'https://www.inhand.com/wp-content/uploads/inhand-smart-vending-system.pdf',
      },
    ],
  },
  // 数字能源手册
  DigitalEnergy_handbook: {
    china: [
      {
        name: '智能化配电网线路状态监测系统',
        url: 'https://www.inhand.com.cn/wp-content/uploads/intelligent-wireless-observing-system.pdf',
      },
    ],
    global: [
      {
        name: 'IWOS Brochure',
        url: 'https://www.inhand.com/wp-content/uploads/iwos.pdf',
      },
    ],
  },
  // 移动性手册
  Mobility_handbook: {
    china: [
      {
        name: '车载与运输产品合集',
        url: 'https://www.inhand.com.cn/wp-content/uploads/mobility.pdf',
      },
    ],
    global: [
      {
        name: 'Product Catalog - Mobility',
        url: 'https://www.inhand.com/wp-content/uploads/product-catalog_mobility.pdf',
      },
    ],
  },
};

// 将项目按照每4个一组进行分组的辅助函数
const groupItems = items => {
  return items.reduce((result, item, index) => {
    const groupIndex = Math.floor(index / 4);
    if (!result[groupIndex]) {
      result[groupIndex] = [];
    }
    result[groupIndex].push(item);
    return result;
  }, []);
};

// 按子类别对文件进行分组的辅助函数
const groupBySubcategory = files => {
  return files.reduce((acc, file) => {
    const subcategory = file.subcategory;
    if (!acc[subcategory]) {
      acc[subcategory] = [];
    }
    acc[subcategory].push(file);
    return acc;
  }, {});
};

// FilesTile组件：用于显示文件列表的主要组件
function FilesTile({ title, params, changelogMaps = [] }) {
  const intl = useIntl(); // 初始化国际化
  const firmwareText = intl.formatMessage({ id: 'Firmware' });
  console.log(params, 'params');

  // 状态管理
  const [filesList, setFilesList] = useState([]); // 文件列表
  const [showLoading, setShowLoading] = useState(true); // 加载状态
  const [showBtn, setShowBtn] = useState(true); // 是否显示"更多"按钮
  const [Array, setArray] = useState([]); // 是否显示"更多"按钮

  // 分页配置
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 8,
  });

  // 判断是否为手册或开发者工具类型
  const isHandbook = params.category === 'handbook';
  const isDeveloperTools = params.category === 'DeveloperTools';

  // 获取手册文件的回调函数
  const getHandbookFiles = useCallback(() => {
    const handbookKey = params.series_id;
    // 根据域名判断区域
    const region =
      window.location.hostname.includes('.cn') ||
      window.location.hostname === 'localhost'
        ? 'china'
        : 'global';

    // 返回对应区域的手册文件列表
    if (HANDBOOK_DOWNLOAD_LINKS[handbookKey]?.[region]) {
      return HANDBOOK_DOWNLOAD_LINKS[handbookKey][region].map(item => ({
        id: `${handbookKey}_${item.name}`,
        name: item.name,
        downloadUrl: item.url,
        external_url: item.url,
      }));
    }
    return [];
  }, [params.series_id]);

  // 请求参数配置
  const parameters = useMemo(
    () => ({
      ...params,
      pageSize: pagination.pageSize,
      pageNumber: pagination.pageNumber,
      product_category: localStorage.getItem('openType'),
    }),
    [pagination, params]
  );

  // 获取文件列表数据
  useEffect(() => {
    if (isHandbook) {
      // 如果是手册类型，直接获取手册文件
      const handbookFiles = getHandbookFiles();
      setFilesList(handbookFiles);
      setShowLoading(false);
      setShowBtn(false);
    } else {
      // 否则从API获取文件列表
      fetchFilesList(parameters).then(data => {
        if (data.result.length === 0) {
          setShowBtn(false);
        }
        setFilesList(prevData => {
          if (!equal(prevData, data.result)) {
            return [...prevData, ...data.result];
          }
          return prevData;
        });
        setShowLoading(false);
      });
    }
  }, [parameters, isHandbook, getHandbookFiles]);

  // 处理文件内容分组
  const groupedContent = useMemo(() => {
    if (isDeveloperTools) {
      const subcategoryGroups = groupBySubcategory(filesList);

      // 获得排序后的数组
      const sortedContent = Object.entries(subcategoryGroups)
        .map(([subcategory, files]) => ({
          subcategory,
          groupedFiles: groupItems(files),
          totalFiles: files.length,
        }))
        .sort((a, b) => a.totalFiles - b.totalFiles);

      // 直接从排序后的内容中提取子分类名称
      const sortedSubcategories = sortedContent.map(item => item.subcategory);
      setArray(sortedSubcategories); // 设置排序后的数组

      return sortedContent;
    } else {
      // 否则作为单一组处理
      return [
        {
          subcategory: null,
          groupedFiles: groupItems(filesList),
        },
      ];
    }
  }, [filesList, isDeveloperTools]);

  // const groupedChangelogsList = useMemo(() => {
  //   if (isDeveloperTools) {
  //     const changelogGroups = Array.map(subcategory => ({
  //       subcategory,
  //       changelogs: [
  //         Array.map(subcategory => ({ [subcategory]: subcategory })),
  //       ],
  //     }));
  //     return changelogGroups;
  //   }
  //   return [{ subcategory: null, changelogs: groupItems(changelogMaps) }];
  // }, [changelogMaps, isDeveloperTools, Array]);

  const groupedChangelogsList = useMemo(() => {
    if (isDeveloperTools) {
      // 直接使用 Array（子分类数组）创建 changelogs
      return [
        {
          subcategory: null,
          changelogs: [
            Array.map(subcategory => ({ [subcategory]: subcategory })),
          ],
        },
      ];
    }
    return [{ subcategory: null, changelogs: groupItems(changelogMaps) }];
  }, [changelogMaps, isDeveloperTools, Array]);

  // 处理"更多"按钮点击事件
  const handleMoreClick = () => {
    setPagination(prevPagination => ({
      ...prevPagination,
      pageNumber: prevPagination.pageNumber + 1,
    }));
  };

  // 渲染组件
  return (
    <>
      {filesList.length > 0 ? (
        <div className={styles.flies_group}>
          {/* 标题区域 */}
          <div className={styles.group_header}>
            {isHandbook ? <FormattedMessage id='handbook' /> : title}
          </div>

          {/* 更新日志区域 */}
          {changelogMaps.length > 0 &&
            (title === firmwareText || isDeveloperTools) && (
              <div className='change_log'>
                {groupedChangelogsList?.map(
                  ({ subcategory, changelogs }, groupIndex) => (
                    <div key={groupIndex}>
                      <div
                        className={styles.files_rows}
                        style={{ marginBottom: 20 }}
                      >
                        {changelogs.map((changelogGroup, index) => (
                          <ChangeLog
                            key={index}
                            changelogs={changelogGroup}
                            isDeveloperTools={isDeveloperTools}
                            category={params.category}
                            series_id={params.series_id}
                          />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

          {/* 文件列表区域 */}
          {groupedContent.map(({ subcategory, groupedFiles }, groupIndex) => (
            <div key={groupIndex}>
              {isDeveloperTools && subcategory && (
                <div className={styles.subcategory_header}></div>
              )}
              <div className={styles.files_rows}>
                {groupedFiles.map((files, index) => (
                  <CardGroup
                    key={index}
                    files={files}
                    title={isHandbook ? '产品宣传册' : title}
                    isHandbook={isHandbook}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* "更多"按钮 */}
          {!isHandbook && filesList.length >= 8 && showBtn && (
            <div style={{ textAlign: 'center', marginTop: '2%' }}>
              <Button
                type='dash'
                size='large'
                onClick={handleMoreClick}
                style={{
                  border: '1px solid #d9d9d9',
                  width: 120,
                  height: 40,
                }}
              >
                More
                <DownOutlined />
              </Button>
            </div>
          )}
        </div>
      ) : (
        showLoading && <Loading />
      )}
    </>
  );
}

// 使用React.memo优化组件，只有当props发生深度变化时才重新渲染
export default React.memo(FilesTile, equal);
